import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Returns a human-readable relative time like "2m 34s".
 * Negative input = overdue: the timer ran out but the case is still open
 * on GlobeMed — "−m:ss" under an hour, then "Overdue 5h+", then
 * "Overdue 3d+" (cases legitimately stay open for weeks). */
export function formatCountdown(secondsLeft: number): string {
  if (secondsLeft <= 0) {
    const overdue = -secondsLeft;
    if (overdue >= 86400) return `Overdue ${Math.floor(overdue / 86400)}d+`;
    if (overdue >= 3600) return `Overdue ${Math.floor(overdue / 3600)}h+`;
    const om = Math.floor(overdue / 60);
    const os = Math.floor(overdue % 60);
    return `−${om}:${os.toString().padStart(2, '0')}`;
  }
  const m = Math.floor(secondsLeft / 60);
  const s = Math.floor(secondsLeft % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Determines urgency level from seconds remaining */
export function getUrgencyLevel(secondsLeft: number): 'calm' | 'watch' | 'urgent' | 'critical' | 'overdue' {
  if (secondsLeft <= 0) return 'overdue';      // past timer, still decidable
  if (secondsLeft <= 60) return 'critical';    // < 1 min
  if (secondsLeft <= 180) return 'urgent';      // < 3 min
  if (secondsLeft <= 480) return 'watch';       // < 8 min
  return 'calm';
}

/** Formats an ISO date string to a local readable format */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Business wording for case outcomes. 'unsuccessful' covers two cases,
 * told apart by submitted_at: submitted-but-not-confirmed vs closed
 * before we could submit. */
export function outcomeLabel(outcome: string | null | undefined, submittedAt?: string | null): string {
  switch (outcome) {
    case 'confirmed': return 'Confirmed';
    case 'unsuccessful':
      return submittedAt ? 'Submitted — Not Confirmed' : 'Case Closed Before Submission';
    case 'missed': return 'Missed — No Response';
    case 'error': return 'Error';
    default: return outcome || '-';
  }
}

/** Patient name from patient_info, with fallback */
export function getPatientName(info: Record<string, unknown>): string {
  return (info.name as string) || (info.patient_name as string) || 'Unknown Patient';
}
